import baseApi from "utils/axios-instance";
import { KnowledgeEntryLookup } from "./selectors";
import {
  CallOutcome,
  FollowUpTask,
  InboundCall,
  KnowledgeEntry,
  ReasonCategory,
  TranscriptTurn,
} from "./types";

const ROOT = "/dashboard/inbound_calls";
const WITH_COOKIES = { withCredentials: true };

/**
 * Wire shape of GET /dashboard/inbound_calls/calls. Every timestamp is integer
 * UTC seconds (DictSerializable.dict()), including the one nested in follow_up_task.
 */
export interface InboundCallRow {
  id: string;
  practice_id: string;
  practice_name: string;
  timezone: string;
  caller_phone: string | null;
  received_at: number;
  status: "IN_PROGRESS" | "PROCESSING" | "COMPLETED" | "FAILED";
  urgency: "NORMAL" | "URGENT";
  outcome: CallOutcome | null;
  reason: ReasonCategory | null;
  recap_text: string | null;
  next_step: string | null;
  caller_name: string | null;
  patient_name: string | null;
  caller_relationship: string | null;
  follow_up_task: {
    description: string;
    patient_info_collected: string[];
    callback_number: string | null;
    due_by: number;
    status: FollowUpTask["status"];
  } | null;
  appointment_type: string | null;
  // The BOOKED action detail, copied onto the row at hangup (InboundCallEventsManager).
  appointment: {
    provider_name: string;
    start: string;
    end: string;
    callback_number: string | null;
  } | null;
  transcript_turns: {
    speaker: TranscriptTurn["speaker"];
    text: string;
    offset_seconds: number;
    knowledge_entry_id: string | null;
  }[];
  recording_url: string | null;
}

interface KnowledgeEntryRow {
  id: string;
  practice_id: string | null;
  question: string;
  answer: string;
  last_updated_ts: number;
}

/** Assigning a seconds value where an ISO string is expected renders as 1970. */
export function secondsToIso(seconds: number): string {
  return new Date(seconds * 1000).toISOString();
}

export function toInboundCall(row: InboundCallRow): InboundCall {
  return {
    id: row.id,
    receivedAt: secondsToIso(row.received_at),
    practiceId: row.practice_id,
    practiceName: row.practice_name,
    timezone: row.timezone,
    status: row.status,
    urgency: row.urgency,
    callerName: row.caller_name ?? "Unknown caller",
    callerPhone: row.caller_phone ?? "",
    ...(row.patient_name ? { patientName: row.patient_name } : {}),
    ...(row.caller_relationship
      ? { callerRelationship: row.caller_relationship }
      : {}),
    reason: row.reason ?? "OTHER",
    recapText:
      row.recap_text ??
      (row.status === "COMPLETED" || row.status === "FAILED"
        ? ""
        : "Processing…"),
    outcome: row.outcome ?? "UNRESOLVED",
    nextStep: row.next_step ?? "",
    ...(row.appointment_type ? { appointmentType: row.appointment_type } : {}),
    ...(row.appointment
      ? {
          appointment: {
            providerName: row.appointment.provider_name,
            startIso: row.appointment.start,
            endIso: row.appointment.end,
            ...(row.appointment.callback_number
              ? { callbackNumber: row.appointment.callback_number }
              : {}),
          },
        }
      : {}),
    ...(row.follow_up_task
      ? {
          followUpTask: {
            description: row.follow_up_task.description,
            patientInfoCollected: row.follow_up_task.patient_info_collected,
            callbackNumber: row.follow_up_task.callback_number ?? "",
            dueBy: secondsToIso(row.follow_up_task.due_by),
            status: row.follow_up_task.status,
          },
        }
      : {}),
    ...(row.recording_url ? { recordingUrl: row.recording_url } : {}),
    transcript: row.transcript_turns.map((turn) => ({
      speaker: turn.speaker,
      text: turn.text,
      offsetSeconds: turn.offset_seconds,
      ...(turn.knowledge_entry_id
        ? { knowledgeEntryId: turn.knowledge_entry_id }
        : {}),
    })),
  };
}

function toKnowledgeEntry(row: KnowledgeEntryRow): KnowledgeEntry {
  const base = {
    id: row.id,
    question: row.question,
    answer: row.answer,
    lastReviewedAt: secondsToIso(row.last_updated_ts),
  };
  return row.practice_id
    ? { ...base, tier: "PRACTICE", practiceId: row.practice_id }
    : { ...base, tier: "ORG" };
}

export async function listInboundCalls(): Promise<InboundCall[]> {
  const response = await baseApi.get<InboundCallRow[]>(
    `${ROOT}/calls`,
    WITH_COOKIES
  );
  return response.data.map(toInboundCall);
}

export async function getInboundCall(callId: string): Promise<InboundCall> {
  const response = await baseApi.get<InboundCallRow>(
    `${ROOT}/calls/${callId}`,
    WITH_COOKIES
  );
  return toInboundCall(response.data);
}

/** The team's real bank, so cited entry ids on real calls resolve (the demo bank cannot). */
export async function listKnowledgeEntries(): Promise<KnowledgeEntry[]> {
  const response = await baseApi.get<KnowledgeEntryRow[]>(
    `${ROOT}/knowledge_entries`,
    WITH_COOKIES
  );
  return response.data.map(toKnowledgeEntry);
}

export function entryLookup(entries: KnowledgeEntry[]): KnowledgeEntryLookup {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  return (id) => byId.get(id);
}

export interface InboundPractice {
  id: string;
  name: string;
}

export interface OrganizationInfo {
  orgSummary: string;
  practiceSummaries: Record<string, string>;
  notCoveredNote: string;
}

interface OrganizationInfoRow {
  org_summary: string;
  per_practice_summaries: { practice_id: string; summary: string }[];
  not_covered_note: string;
}

function toOrganizationInfo(row: OrganizationInfoRow): OrganizationInfo {
  return {
    orgSummary: row.org_summary,
    notCoveredNote: row.not_covered_note,
    practiceSummaries: Object.fromEntries(
      row.per_practice_summaries.map((item) => [item.practice_id, item.summary])
    ),
  };
}

export async function listInboundPractices(): Promise<InboundPractice[]> {
  const response = await baseApi.get<InboundPractice[]>(
    `${ROOT}/practices`,
    WITH_COOKIES
  );
  return response.data;
}

export async function getOrganizationInfo(): Promise<OrganizationInfo> {
  const response = await baseApi.get<OrganizationInfoRow>(
    `${ROOT}/organization_info`,
    WITH_COOKIES
  );
  return toOrganizationInfo(response.data);
}

export async function saveOrganizationInfo(
  info: OrganizationInfo
): Promise<OrganizationInfo> {
  const response = await baseApi.put<OrganizationInfoRow>(
    `${ROOT}/organization_info`,
    {
      org_summary: info.orgSummary,
      not_covered_note: info.notCoveredNote,
      per_practice_summaries: Object.entries(info.practiceSummaries)
        .filter(([, summary]) => summary.trim() !== "")
        .map(([practice_id, summary]) => ({ practice_id, summary })),
    },
    WITH_COOKIES
  );
  return toOrganizationInfo(response.data);
}

export async function createKnowledgeEntry(draft: {
  question: string;
  answer: string;
  practiceId?: string;
}): Promise<KnowledgeEntry> {
  const response = await baseApi.post<KnowledgeEntryRow>(
    `${ROOT}/knowledge_entries`,
    {
      question: draft.question,
      answer: draft.answer,
      practice_id: draft.practiceId ?? null,
    },
    WITH_COOKIES
  );
  return toKnowledgeEntry(response.data);
}

export async function deleteKnowledgeEntry(entryId: string): Promise<void> {
  await baseApi.delete(`${ROOT}/knowledge_entries/${entryId}`, WITH_COOKIES);
}

export async function updateKnowledgeEntry(entry: {
  id: string;
  question: string;
  answer: string;
  practiceId?: string;
}): Promise<KnowledgeEntry> {
  const response = await baseApi.put<KnowledgeEntryRow>(
    `${ROOT}/knowledge_entries/${entry.id}`,
    {
      question: entry.question,
      answer: entry.answer,
      practice_id: entry.practiceId ?? null,
    },
    WITH_COOKIES
  );
  return toKnowledgeEntry(response.data);
}

export interface AppointmentCategoryRow {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
}

export async function listAppointmentCategories(): Promise<AppointmentCategoryRow[]> {
  const response = await baseApi.get<AppointmentCategoryRow[]>(
    `${ROOT}/appointment_categories`,
    WITH_COOKIES
  );
  return response.data;
}

export async function createAppointmentCategory(
  category: Omit<AppointmentCategoryRow, "id">
): Promise<AppointmentCategoryRow> {
  const response = await baseApi.post<AppointmentCategoryRow>(
    `${ROOT}/appointment_categories`,
    { ...category, provider_ids: [] },
    WITH_COOKIES
  );
  return response.data;
}

export async function updateAppointmentCategory(
  category: AppointmentCategoryRow
): Promise<AppointmentCategoryRow> {
  const response = await baseApi.put<AppointmentCategoryRow>(
    `${ROOT}/appointment_categories/${category.id}`,
    {
      name: category.name,
      description: category.description,
      duration_minutes: category.duration_minutes,
    },
    WITH_COOKIES
  );
  return response.data;
}

export async function deleteAppointmentCategory(categoryId: string): Promise<void> {
  await baseApi.delete(
    `${ROOT}/appointment_categories/${categoryId}`,
    WITH_COOKIES
  );
}
